import type { Rung, TeamId } from '@badger-sentinel/protocol';

/**
 * MIL-STD-2525E / APP-6(E) symbol codes, from a `typeId` (Q4).
 *
 * > **A constant milsymbol SIDC mapping, generated exactly as the mockup does.**
 *
 * "Exactly as the mockup does" is the load-bearing half. The two vendored
 * renderers (`vendor/maps/region-map.js` 131–180, `vendor/maps/orbital-map.js`
 * 60–79) already build a code, and they build it the same way:
 *
 * ```
 * 100 · identity(1) · symbolSet(2) · status(1) · 000 · entity(6) · mod1(2) · mod2(2)
 * ```
 *
 * `region-map` varies the status digit and hardcodes `mod2`; `orbital-map`
 * hardcodes the status digit and varies `mod2`. One construction covers both, so
 * this module is the *third* caller of a shape that already had two — which is
 * what makes it an extraction rather than a speculation (M5-D3).
 *
 * ## The entity codes are lifted, never invented
 *
 * `SIDC_ENTITIES` is copied out of the two vendored files verbatim. A `typeId`
 * with no clean analogue there maps to a **frame** — entity `000000`, which
 * milsymbol renders as a correct affiliation frame with no icon in it. That is
 * an honest answer ("a friendly land unit, kind unstated") where a guessed
 * six-digit code would be a confident wrong one, and it is the same symbol the
 * mockup itself uses for legends and cluster badges.
 *
 * Cyber assets are all frames today: the design's cyber symbology lives in
 * `cyber-map.js`, which Phase 7 vendors. This is the module that gains those
 * rows when it does.
 *
 * ## Indistinguishability
 *
 * `sidcFor` reads `typeId`, affiliation and `rung`, all three of which a contact
 * carries. Nothing here can tell a phantom from a real contact, and nothing here
 * may learn to — must-not #5 is a property of this function as much as of the
 * component that calls it.
 */

/** `[symbolSet, entity, mod1?, mod2?]`, lifted from the vendored renderers. */
type EntityCode = readonly [string, string, string?, string?];

export const SIDC_ENTITIES = Object.freeze({
  // --- region-map.js: air, sea, land ---
  air: ['01', '110104'], // Air · fixed wing · fighter
  uav: ['01', '110300'], // Air · unmanned aerial vehicle
  naval: ['30', '120200'], // Sea surface · surface combatant, line
  sub: ['35', '110100'], // Sea subsurface · submarine
  missile: ['10', '130700'], // Land unit · fires · missile
  airdefense: ['10', '130100'], // Land unit · fires · air defence
  ground: ['10', '121100'], // Land unit · manoeuvre · infantry
  sof: ['10', '121700'], // Land unit · special forces
  radar: ['15', '220300'], // Land equipment · radar
  ew: ['10', '150500'], // Land unit · intelligence · electronic warfare
  c2: ['10', '110000', '10'], // Land unit · command and control (C2 modifier)
  logistics: ['10', '160000'], // Land unit · sustainment
  civ: ['11', '110000'], // Land civilian unit/organization
  ssa: ['10', '111300'], // Land unit · C2 · space
  daasat: ['10', '142200'], // Land unit · air and missile defense
  frame: ['10', '000000'], // frame only — land

  // --- orbital-map.js: space ---
  comms: ['05', '111100'], // Space · communications satellite
  nav: ['05', '111400'], // Space · navigational satellite
  isr: ['05', '111500'], // Space · reconnaissance satellite
  msnwarn: ['05', '110600', '00', '02'], // Space · satellite · infrared
  ewsat: ['05', '110600', '00', '06'], // Space · satellite · electromagnetic warfare
  asatCo: ['05', '110800'], // Space · antisatellite weapon
  rpo: ['05', '110100'], // Space · space vehicle
  spaceFrame: ['05', '000000'], // frame only — space
} as const satisfies Record<string, EntityCode>);

export type SidcIcon = keyof typeof SIDC_ENTITIES;

/**
 * Built-in `typeId` to icon key (`packages/rules/src/data/asset-types.ts`).
 *
 * A scenario may declare its own types (§10.4.2's `DF_21_TEL`), and a type not
 * listed here falls back by segment rather than failing — a marker that renders
 * as a bare frame is legible; one that throws takes the rail down.
 */
export const SIDC_ICON_FOR_TYPE: Readonly<Record<string, SidcIcon>> = Object.freeze({
  // Space
  PNT_SATELLITE: 'nav',
  ISR_SATELLITE_OPTICAL: 'isr',
  ISR_SATELLITE_RADAR: 'isr',
  SIGINT_SATELLITE: 'isr',
  MISSILE_WARNING_SATELLITE: 'msnwarn',
  SDA_SATELLITE: 'isr',
  IMAGING_SATELLITE_EO: 'isr',
  IMAGING_SATELLITE_RADAR: 'isr',
  IMAGING_SATELLITE_HYPERSPECTRAL: 'isr',
  GMTI_RADAR_SATELLITE: 'isr',
  COMMUNICATIONS_RELAY: 'comms',
  ORBITAL_JAMMER: 'ewsat',
  RPO_VEHICLE: 'rpo',
  COORBITAL_KKV: 'asatCo',
  SUB_SATELLITE: 'rpo',
  // No meteorological entity in either vendored map, and none invented here.
  WEATHER_SATELLITE: 'spaceFrame',

  // Ground
  SDA_RADAR_SITE: 'ssa',
  SDA_OPTICAL_SITE: 'ssa',
  PASSIVE_RF_SITE: 'radar',
  TTC_STATION: 'c2',
  DOWNLINK_STATION: 'c2',
  MISSILE_WARNING_GROUND_STATION: 'radar',
  GROUND_JAMMER_FIXED: 'ew',
  GROUND_JAMMER_MOBILE: 'ew',
  GROUND_DAZZLER: 'ew',
  DA_ASAT_BATTERY: 'daasat',
  BALLISTIC_MISSILE_TEL: 'missile',
  BALLISTIC_MISSILE_SILO: 'missile',
  HGV_LAUNCHER: 'missile',
  INTERCEPTOR_BATTERY: 'airdefense',
  SPACE_LAUNCH_SITE: 'frame',

  // Cyber — Phase 7 vendors `cyber-map.js` and supplies these.
  CYBER_OFFENSIVE: 'frame',
  CYBER_DEFENSIVE: 'frame',
  CYBER_COMBINED: 'frame',
});

/** Standard identity digit: friend / hostile / unknown. */
const IDENTITY: Readonly<Record<SidcAffiliation, string>> = Object.freeze({
  FRIEND: '3',
  HOSTILE: '6',
  UNKNOWN: '1',
});

export type SidcAffiliation = 'FRIEND' | 'HOSTILE' | 'UNKNOWN';
export type SidcSegment = 'SPACE' | 'GROUND' | 'CYBER';

export function affiliationOf(owner: TeamId | null, viewer: TeamId): SidcAffiliation {
  if (owner === null) return 'UNKNOWN';
  return owner === viewer ? 'FRIEND' : 'HOSTILE';
}

export function iconFor(typeId: string, segment: SidcSegment): SidcIcon {
  return SIDC_ICON_FOR_TYPE[typeId] ?? (segment === 'SPACE' ? 'spaceFrame' : 'frame');
}

/**
 * The 20-character code.
 *
 * The status digit is `1` — the standard's "anticipated/planned", which milsymbol
 * draws as a dashed frame — for a contact merely `FOUND`, and `0` once it is
 * `FIXED` or `TRACKED`. `region-map.js` makes the same call for the same reason:
 * an unconfirmed track should not draw with the certainty of a held one. `rung`
 * is on every `ContactView`, so this reads nothing a phantom lacks.
 */
export function sidcFor(args: {
  readonly typeId: string;
  readonly segment: SidcSegment;
  readonly affiliation: SidcAffiliation;
  readonly rung?: Rung | null;
}): string {
  const entity: EntityCode = SIDC_ENTITIES[iconFor(args.typeId, args.segment)];
  const identity = IDENTITY[args.affiliation];
  const status = args.rung === 'FOUND' ? '1' : '0';
  const [set, code, mod1, mod2] = entity;
  return '100' + identity + set + status + '000' + code + (mod1 ?? '00') + (mod2 ?? '00');
}
